import type { FC } from 'react';
import { Button, Accordion, AccordionItem } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import type { PreDrillTargetZoneSuggestions } from 'types/api-responses';
import { SCORING_RULE_LABELS, pctScore } from './utils';
import styles from './agent-output-panel.module.scss';

interface AgentOutputPanelProps {
  suggestions: PreDrillTargetZoneSuggestions;
  onClose: () => void;
}

// Read-only agent reasoning trace (right sidebar): summary, key evidence, and
// a per-candidate accordion of scored factors.
export const AgentOutputPanel: FC<AgentOutputPanelProps> = ({
  suggestions,
  onClose,
}) => (
  <div className={styles.panel}>
    <div className={styles.header}>
      <div>
        <h2 className={styles.title}>Agent Details</h2>
        <p className={styles.subtitle}>Target Zone Identification</p>
      </div>
      <Button
        kind="ghost"
        size="sm"
        hasIconOnly
        renderIcon={Close}
        iconDescription="Close"
        onClick={onClose}
      />
    </div>

    <div className={styles.body}>
      {suggestions.narrativeSummary && (
        <section className={styles.summary}>
          <h3 className={styles.summaryHeading}>Summary</h3>
          <p className={styles.summaryText}>{suggestions.narrativeSummary}</p>
        </section>
      )}

      {suggestions.keyEvidence.length > 0 && (
        <section className={styles.summary}>
          <h3 className={styles.summaryHeading}>Key Evidence</h3>
          <ul className={styles.evidence}>
            {suggestions.keyEvidence.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </section>
      )}

      <Accordion>
        {suggestions.candidates.map((candidate) => (
          <AccordionItem
            key={candidate.geologicIntervalId}
            open={candidate.rank === 1}
            title={
              <span className={styles.candidateTitle}>
                {candidate.geologicIntervalName}
                {candidate.rank === 1 && (
                  <span className={styles.recommended}>(Recommended)</span>
                )}
                <span className={styles.candidateScore}>
                  {pctScore(candidate.score)}
                </span>
              </span>
            }
          >
            <dl className={styles.ruleList}>
              {Object.entries(candidate.breakdown ?? {}).map(
                ([rule, value]) => (
                  <div key={rule} className={styles.rule}>
                    <span className={styles.ruleName}>
                      {SCORING_RULE_LABELS[rule] ?? rule}
                    </span>
                    <span className={styles.ruleScore}>
                      {pctScore(value.score)}
                    </span>
                  </div>
                )
              )}
            </dl>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </div>
);
